import { clsx } from "clsx";
import {
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  Gift,
  HandCoins,
  Plus,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { PERMISSIONS } from "@/auth/permissions";
import { Button } from "@/components/common/Button";
import { Card } from "@/components/common/Card";
import { EmptyState } from "@/components/common/EmptyState";
import { Select } from "@/components/common/Select";
import { Spinner } from "@/components/common/Spinner";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  AddContractBonusModal,
  DeleteContractBonusModal,
} from "@/components/reports/ContractBonusModals";
import { formatCurrency, MONTHS } from "@/components/reports/format";
import { useHasPermission } from "@/hooks/usePermission";
import { useCompanyResults } from "@/hooks/useReports";
import type { CompanyMonth, ContractBonus } from "@/types/reports";

const MONTHS_SHORT = [
  "sty",
  "lut",
  "mar",
  "kwi",
  "maj",
  "cze",
  "lip",
  "sie",
  "wrz",
  "paź",
  "lis",
  "gru",
];

/** "0" selects the whole year. */
const WHOLE_YEAR = "0";

const PERIOD_OPTIONS = [
  { value: WHOLE_YEAR, label: "Cały rok" },
  ...MONTHS.map((label, i) => ({
    value: String(i + 1),
    label,
  })),
];

function yearOptions() {
  const current = new Date().getFullYear();
  return Array.from({ length: current - 2023 }, (_, i) => {
    const year = String(current - i);
    return { value: year, label: year };
  });
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(value);
}

function share(part: number, total: number) {
  return total > 0 ? part / total : 0;
}

function hasData(m: CompanyMonth) {
  return m.agentCount > 0 || m.bonuses > 0;
}

// ============================
// Stat tiles
// ============================

function Delta({
  current,
  previous,
  comparedTo,
}: {
  current: number;
  previous?: number;
  comparedTo: string;
}) {
  if (previous === undefined || previous === 0) {
    return null;
  }
  const change = (current - previous) / Math.abs(previous);
  const up = change >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-0.5 text-xs font-medium",
        up
          ? "text-emerald-700 dark:text-emerald-400"
          : "text-red-700 dark:text-red-400",
      )}
    >
      <Icon size={14} aria-hidden />
      {up ? "+" : ""}
      {formatPercent(change)}
      <span className="sr-only"> względem {comparedTo}</span>
    </span>
  );
}

interface StatTileProps {
  label: string;
  value: number;
  previous?: number;
  comparedTo: string;
  icon: typeof Banknote;
  emphasis?: boolean;
  footnote?: string;
  /** Shown under the amount, e.g. a button to add an entry. */
  action?: ReactNode;
}

function StatTile({
  label,
  value,
  previous,
  comparedTo,
  icon: Icon,
  emphasis,
  footnote,
  action,
}: StatTileProps) {
  return (
    <Card className={clsx(emphasis && "ring-2 ring-adara-600/30")}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-dim">{label}</p>
        <Icon size={18} className="text-faint" aria-hidden />
      </div>
      <p
        className={clsx(
          "mt-2 font-bold tabular-nums text-main",
          emphasis ? "text-3xl" : "text-2xl",
        )}
      >
        {formatCurrency(value)}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Delta current={value} previous={previous} comparedTo={comparedTo} />
        {footnote && <span className="text-xs text-faint">{footnote}</span>}
      </div>
      {action && <div className="mt-3">{action}</div>}
    </Card>
  );
}

// ============================
// Margin trend (single series bar chart)
// ============================

function MarginTrend({
  trend,
  selected,
  onSelect,
}: {
  trend: CompanyMonth[];
  /** month null = the whole year is selected: the chart shows its months. */
  selected: { year: number; month: number | null };
  onSelect: (year: number, month: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(...trend.map((m) => m.margin), 1);
  const hoveredMonth = hovered === null ? null : trend[hovered];

  return (
    <Card>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-main">
          {selected.month === null
            ? `Marża firmy — ${selected.year}`
            : "Marża firmy — ostatnie 12 miesięcy"}
        </h2>
        <span className="text-xs text-faint">
          Kliknij słupek, aby wybrać miesiąc
        </span>
      </div>

      <div className="relative">
        {/* Recessive gridlines: top value and the midpoint */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-48">
          {[0, 0.5].map((f) => (
            <div
              key={f}
              className="absolute inset-x-0 border-t border-dashed border-edge-soft"
              style={{ top: `${f * 100}%` }}
            >
              <span className="absolute -top-2 right-0 w-15 text-right text-[10px] tabular-nums text-faint">
                {formatCurrency(max * (1 - f))}
              </span>
            </div>
          ))}
        </div>

        <div
          role="group"
          aria-label="Marża firmy w kolejnych miesiącach"
          className="relative mr-16 flex h-48 items-end gap-1 border-b border-edge-soft sm:gap-2"
          onMouseLeave={() => {
            setHovered(null);
          }}
        >
          {trend.map((m, i) => {
            const isSelected =
              m.year === selected.year && m.month === selected.month;
            const height = Math.max(m.margin, 0) / max;
            return (
              <button
                key={`${m.year}-${m.month}`}
                type="button"
                // The hit target is the whole column, not just the bar.
                className="group flex h-full flex-1 items-end justify-center focus-visible:outline-none"
                onMouseEnter={() => {
                  setHovered(i);
                }}
                onFocus={() => {
                  setHovered(i);
                }}
                onBlur={() => {
                  setHovered(null);
                }}
                onClick={() => {
                  onSelect(m.year, m.month);
                }}
                aria-label={`${MONTHS[m.month - 1]} ${m.year}: marża ${formatCurrency(m.margin)}`}
                aria-pressed={isSelected}
              >
                <span
                  className={clsx(
                    "block w-full max-w-10 rounded-t transition-colors group-focus-visible:ring-2 group-focus-visible:ring-adara-500",
                    isSelected
                      ? "bg-adara-600 dark:bg-adara-300"
                      : "bg-adara-300 group-hover:bg-adara-400 dark:bg-adara-600 dark:group-hover:bg-adara-500",
                  )}
                  style={{
                    height: `${height * 100}%`,
                    minHeight: m.margin > 0 ? 2 : 0,
                  }}
                />
              </button>
            );
          })}
        </div>

        <div className="mt-1 mr-16 flex gap-1 sm:gap-2">
          {trend.map((m) => (
            <span
              key={`${m.year}-${m.month}`}
              className="flex-1 text-center text-[10px] text-faint sm:text-xs"
            >
              {MONTHS_SHORT[m.month - 1]}
            </span>
          ))}
        </div>

        {hoveredMonth && hovered !== null && (
          <div
            role="tooltip"
            className="pointer-events-none absolute top-2 z-10 w-52 -translate-x-1/2 rounded-lg border border-edge-soft bg-panel p-3 text-xs shadow-lg"
            style={{
              left: `clamp(6.5rem, calc((100% - 4rem) * ${(hovered + 0.5) / trend.length}), calc(100% - 6.5rem))`,
            }}
          >
            <p className="mb-1.5 font-semibold text-main">
              {MONTHS[hoveredMonth.month - 1]} {hoveredMonth.year}
            </p>
            <dl className="space-y-0.5">
              <div className="flex justify-between gap-2">
                <dt className="text-dim">Marża</dt>
                <dd className="font-semibold tabular-nums text-main">
                  {formatCurrency(hoveredMonth.margin)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-dim">Przychód</dt>
                <dd className="tabular-nums text-sub">
                  {formatCurrency(hoveredMonth.revenue)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-dim">Wypłaty agentom</dt>
                <dd className="tabular-nums text-sub">
                  {formatCurrency(hoveredMonth.agentPayouts)}
                </dd>
              </div>
              {hoveredMonth.bonuses > 0 && (
                <div className="flex justify-between gap-2">
                  <dt className="text-dim">Premie z kontraktów</dt>
                  <dd className="tabular-nums text-sub">
                    {formatCurrency(hoveredMonth.bonuses)}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>

      <details className="mt-4 text-sm">
        <summary className="cursor-pointer text-dim hover:text-main">
          Pokaż jako tabelę
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-md text-left">
            <thead className="text-xs text-dim">
              <tr>
                <th className="py-1.5 font-medium">Miesiąc</th>
                <th className="py-1.5 text-right font-medium">Przychód</th>
                <th className="py-1.5 text-right font-medium">Wypłaty</th>
                <th className="py-1.5 text-right font-medium">Premie</th>
                <th className="py-1.5 text-right font-medium">Marża</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge-soft tabular-nums">
              {[...trend].reverse().map((m) => (
                <tr key={`${m.year}-${m.month}`}>
                  <td className="py-1.5 text-sub">
                    {MONTHS[m.month - 1]} {m.year}
                  </td>
                  <td className="py-1.5 text-right text-sub">
                    {formatCurrency(m.revenue)}
                  </td>
                  <td className="py-1.5 text-right text-sub">
                    {formatCurrency(m.agentPayouts)}
                  </td>
                  <td className="py-1.5 text-right text-sub">
                    {formatCurrency(m.bonuses)}
                  </td>
                  <td className="py-1.5 text-right font-medium text-main">
                    {formatCurrency(m.margin)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Card>
  );
}

// ============================
// Breakdown tables
// ============================

function ShareBar({ value }: { value: number }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="w-12 text-right tabular-nums text-sub">
        {formatPercent(value)}
      </span>
      <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-subtle sm:block">
        <span
          className="block h-full rounded-full bg-adara-500 dark:bg-adara-300"
          style={{ width: `${Math.max(value, 0) * 100}%` }}
        />
      </span>
    </div>
  );
}

// ============================
// Contract bonuses
// ============================

function BonusesCard({
  bonuses,
  wholeYear,
  canAdd,
  canDelete,
  onAdd,
  onDelete,
}: {
  bonuses: ContractBonus[];
  wholeYear: boolean;
  canAdd: boolean;
  canDelete: boolean;
  onAdd: () => void;
  onDelete: (bonus: ContractBonus) => void;
}) {
  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-main">
            Premie i wypłaty z kontraktów
          </h2>
          <p className="text-xs text-faint">
            W całości zysk firmy — wliczone w marżę.
          </p>
        </div>
        {canAdd && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Plus size={16} aria-hidden />}
            onClick={onAdd}
          >
            Dodaj premię
          </Button>
        )}
      </div>
      {bonuses.length === 0 ? (
        <p className="py-2 text-sm text-dim">
          Brak premii w tym {wholeYear ? "roku" : "miesiącu"}.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-96 text-left text-sm">
            <thead className="text-xs text-dim">
              <tr>
                {wholeYear && <th className="py-2 font-medium">Miesiąc</th>}
                <th className="py-2 font-medium">Opis</th>
                <th className="py-2 font-medium">Towarzystwo</th>
                <th className="py-2 text-right font-medium">Kwota</th>
                {canDelete && (
                  <th className="w-10 py-2">
                    <span className="sr-only">Akcje</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-edge-soft tabular-nums">
              {bonuses.map((b) => (
                <tr key={b.id}>
                  {wholeYear && (
                    <td className="py-2 text-sub">{MONTHS[b.month - 1]}</td>
                  )}
                  <td className="py-2 font-medium text-main">
                    {b.description}
                  </td>
                  <td className="py-2 text-sub">{b.insurerName ?? "—"}</td>
                  <td className="py-2 text-right font-medium text-main">
                    {formatCurrency(b.amount, 2)}
                  </td>
                  {canDelete && (
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          onDelete(b);
                        }}
                        className="rounded p-1 text-faint hover:bg-subtle hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adara-500"
                        aria-label={`Usuń premię: ${b.description}`}
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ============================
// Main Page
// ============================

export function CompanyResultsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  // null = the whole year.
  const [month, setMonth] = useState<number | null>(now.getMonth() + 1);
  const { data, isLoading, isFetching } = useCompanyResults(year, month);
  const canAddBonus = useHasPermission(PERMISSIONS.REPORTS.CREATE);
  const canDeleteBonus = useHasPermission(PERMISSIONS.REPORTS.DELETE);
  const [addingBonus, setAddingBonus] = useState(false);
  const [deletingBonus, setDeletingBonus] = useState<ContractBonus | null>(
    null,
  );

  // The current month is usually not imported yet: on first load jump to the latest month with data.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (!data || autoPicked.current) {
      return;
    }
    autoPicked.current = true;
    if (!hasData(data.month)) {
      const latest = [...data.trend].reverse().find(hasData);
      if (latest) {
        setYear(latest.year);
        setMonth(latest.month);
      }
    }
  }, [data]);

  const selectMonth = (y: number, m: number) => {
    setYear(y);
    setMonth(m);
  };

  const wholeYear = month === null;
  const current = data?.month;
  const previous = data?.previousMonth ?? undefined;
  const comparedTo = wholeYear ? "poprzedniego roku" : "poprzedniego miesiąca";
  const periodLabel = wholeYear
    ? `${year}`
    : `${MONTHS[month - 1] ?? ""} ${year}`;
  // In the whole-year view a new bonus defaults to the current month of this year, else December.
  const bonusMonth =
    month ?? (year === now.getFullYear() ? now.getMonth() + 1 : 12);
  const commissionMargin = current ? current.margin - current.bonuses : 0;

  return (
    <div className="animate-fade-in space-y-4">
      <PageHeader
        title="Wyniki firmy"
        description="Prowizje od towarzystw minus wypłaty agentom, plus premie z kontraktów. Liczone ze wszystkich rozliczeń, także jeszcze niezatwierdzonych."
      />

      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={month === null ? WHOLE_YEAR : String(month)}
            onChange={(v) => {
              setMonth(v === WHOLE_YEAR ? null : Number(v));
            }}
            options={PERIOD_OPTIONS}
            className="w-44"
            size="sm"
          />
          <Select
            value={String(year)}
            onChange={(v) => {
              setYear(Number(v));
            }}
            options={yearOptions()}
            className="w-32"
            size="sm"
          />
          {isFetching && !isLoading && <Spinner size="sm" />}
        </div>
      </Card>

      {isLoading || !data || !current ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Przychód z prowizji"
              value={current.revenue}
              previous={previous?.revenue}
              comparedTo={comparedTo}
              icon={Banknote}
              footnote={`${current.policyCount} polis`}
            />
            <StatTile
              label="Wypłaty agentom"
              value={current.agentPayouts}
              previous={previous?.agentPayouts}
              comparedTo={comparedTo}
              icon={HandCoins}
              footnote={`${current.agentCount} agentów`}
            />
            <StatTile
              label="Premie z kontraktów"
              value={current.bonuses}
              previous={previous?.bonuses}
              comparedTo={comparedTo}
              icon={Gift}
              footnote={
                data.bonuses.length > 0
                  ? `${data.bonuses.length} ${data.bonuses.length === 1 ? "wpłata" : "wpłat"}`
                  : undefined
              }
              action={
                canAddBonus ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Plus size={16} aria-hidden />}
                    onClick={() => {
                      setAddingBonus(true);
                    }}
                  >
                    Dodaj premię
                  </Button>
                ) : undefined
              }
            />
            <StatTile
              label="Marża firmy"
              value={current.margin}
              previous={previous?.margin}
              comparedTo={comparedTo}
              icon={TrendingUp}
              emphasis
              footnote={
                current.revenue > 0
                  ? `${formatPercent(share(commissionMargin, current.revenue))} prowizji${current.bonuses > 0 ? " + premie" : ""}`
                  : undefined
              }
            />
          </div>

          <MarginTrend
            trend={data.trend}
            selected={{ year, month }}
            onSelect={selectMonth}
          />

          {!hasData(current) ? (
            <Card>
              <EmptyState
                icon={<TrendingUp size={32} aria-hidden />}
                title={`Brak rozliczeń: ${periodLabel}`}
                description={
                  wholeYear
                    ? "Wynik pojawi się po zaimportowaniu prowizji od towarzystw z tego roku."
                    : "Wynik pojawi się po zaimportowaniu prowizji od towarzystw za ten miesiąc."
                }
                action={
                  canAddBonus ? (
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Plus size={16} aria-hidden />}
                      onClick={() => {
                        setAddingBonus(true);
                      }}
                    >
                      Dodaj premię z kontraktu
                    </Button>
                  ) : undefined
                }
              />
            </Card>
          ) : (
            <div className="space-y-4">
              <BonusesCard
                bonuses={data.bonuses}
                wholeYear={wholeYear}
                canAdd={canAddBonus}
                canDelete={canDeleteBonus}
                onAdd={() => {
                  setAddingBonus(true);
                }}
                onDelete={setDeletingBonus}
              />
              {data.agents.length > 0 && (
                <Card>
                  <h2 className="mb-3 text-base font-semibold text-main">
                    Agenci
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-120 text-left text-sm">
                      <thead className="text-xs text-dim">
                        <tr>
                          <th className="py-2 font-medium">Agent</th>
                          <th className="py-2 text-right font-medium">
                            Polisy
                          </th>
                          <th className="py-2 text-right font-medium">
                            Przychód
                          </th>
                          <th className="py-2 text-right font-medium">Marża</th>
                          <th className="py-2 text-right font-medium">
                            Udział w marży
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-edge-soft tabular-nums">
                        {data.agents.map((a) => (
                          <tr key={a.agentId}>
                            <td className="py-2 font-medium text-main">
                              {a.agentName}
                            </td>
                            <td className="py-2 text-right text-sub">
                              {a.policyCount}
                            </td>
                            <td className="py-2 text-right text-sub">
                              {formatCurrency(a.revenue)}
                            </td>
                            <td className="py-2 text-right font-medium text-main">
                              {formatCurrency(a.margin)}
                            </td>
                            <td className="py-2">
                              <ShareBar
                                value={share(a.margin, commissionMargin)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {data.insurers.length > 0 && (
                <Card>
                  <h2 className="mb-3 text-base font-semibold text-main">
                    Towarzystwa ubezpieczeniowe
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-96 text-left text-sm">
                      <thead className="text-xs text-dim">
                        <tr>
                          <th className="py-2 font-medium">Towarzystwo</th>
                          <th className="py-2 text-right font-medium">
                            Polisy
                          </th>
                          <th className="py-2 text-right font-medium">
                            Prowizja
                          </th>
                          <th className="py-2 text-right font-medium">
                            Udział
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-edge-soft tabular-nums">
                        {data.insurers.map((i) => (
                          <tr key={i.insurerName}>
                            <td className="py-2 font-medium text-main">
                              {i.insurerName}
                            </td>
                            <td className="py-2 text-right text-sub">
                              {i.policyCount}
                            </td>
                            <td className="py-2 text-right text-sub">
                              {formatCurrency(i.revenue)}
                            </td>
                            <td className="py-2">
                              <ShareBar
                                value={share(
                                  i.revenue,
                                  data.insurers.reduce(
                                    (s, x) => s + x.revenue,
                                    0,
                                  ),
                                )}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      <AddContractBonusModal
        isOpen={addingBonus}
        onClose={() => {
          setAddingBonus(false);
        }}
        year={year}
        defaultMonth={bonusMonth}
        yearOptions={yearOptions()}
      />
      <DeleteContractBonusModal
        bonus={deletingBonus}
        onClose={() => {
          setDeletingBonus(null);
        }}
      />
    </div>
  );
}
